import React from 'react';
import { useTranslation } from 'react-i18next';

import MarketingPageFooter from '../../components/marketing/MarketingPageFooter';
import MarketingLandingNav, { MARKETING_NAV_TOP_OFFSET } from '../../components/marketing/MarketingLandingNav';
import LocalizedPolicyBody, { hasLocalizedPolicyBody } from '../../components/PolicyContent/LocalizedPolicyBody';
import PolicyDocumentNav from '../../components/PolicyContent/PolicyDocumentNav';
import PolicyDocumentRenderer from '../../components/PolicyContent/PolicyDocumentRenderer';
import PolicyDocumentToc from '../../components/PolicyContent/PolicyDocumentToc';
import { SEO } from '../../shared/components/SEO';
import { buildBreadcrumbJsonLd } from '../../shared/seo/breadcrumbJsonLd';
import { getPolicyDocument, buildPolicyToc, type PolicyId } from '../../content/legal';
import { getLocalizedPolicySeo } from '../../content/i18n/marketingPageSeo';
import { isEnglishLocale } from '../../i18n/localeUtils';

interface PolicyDocumentViewProps {
  policyId: PolicyId;
  onNavigate?: (view: string) => void;
}

const PolicyDocumentView: React.FC<PolicyDocumentViewProps> = ({ policyId, onNavigate }) => {
  const { i18n } = useTranslation();
  const useLocalizedBody = !isEnglishLocale(i18n.language) && hasLocalizedPolicyBody(policyId);

  if (useLocalizedBody) {
    return (
      <div className="policy-doc-layout policy-doc-layout--localized">
        <div className="policy-doc-layout__content policy-doc-layout__content--full">
          <LocalizedPolicyBody policyId={policyId} onNavigate={onNavigate} />
        </div>
      </div>
    );
  }

  const doc = getPolicyDocument(policyId);
  const toc = buildPolicyToc(doc.sections);

  return (
    <div className="policy-doc-layout">
      <PolicyDocumentToc entries={toc} />
      <div className="policy-doc-layout__content">
        <PolicyDocumentRenderer
          eyebrow={doc.eyebrow}
          title={doc.title}
          effectiveDate={doc.effectiveDate}
          lastUpdated={doc.lastUpdated}
          summary={doc.summary}
          sections={doc.sections}
        />
        <PolicyDocumentNav currentPolicy={policyId} onNavigate={onNavigate} />
      </div>
    </div>
  );
};

interface LegalPolicyPageProps {
  policyId: PolicyId;
  onNavigate: (view: string) => void;
}

const LegalPolicyPage: React.FC<LegalPolicyPageProps> = ({ policyId, onNavigate }) => {
  const { t, i18n } = useTranslation();
  const seo = getLocalizedPolicySeo(t, policyId);

  return (
    <div className={`policy-doc-page policy-doc-page--${policyId} min-h-screen flex flex-col`}>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        jsonLd={buildBreadcrumbJsonLd([
          { name: t('common.home', { defaultValue: 'Home' }), path: '/' },
          { name: seo.breadcrumb },
        ])}
      />

      <MarketingLandingNav onNavigate={onNavigate} variant="marketing" />

      <main className={`policy-doc-page__main ${MARKETING_NAV_TOP_OFFSET}`} key={i18n.language}>
        <PolicyDocumentView policyId={policyId} onNavigate={onNavigate} />
      </main>

      <MarketingPageFooter onNavigate={onNavigate} theme={policyId} />
    </div>
  );
};

export { PolicyDocumentView, LegalPolicyPage };
export default LegalPolicyPage;
